/**
 * @fileoverview Guided Filter 管线创建模块
 * 创建计算管线和BindGroup
 * 
 * @author 织
 * @version 1.0.0
 */

import { generateGuidedFilterShader, generateDetailExtractionShader } from './guided-filter-shaders.js';

/**
 * 创建Guided Filter计算管线
 * @param {GPUDevice} device - WebGPU设备
 * @param {GuidedFilterParams} params - 算法参数
 * @param {GPUBindGroupLayout} bindGroupLayout - BindGroup布局
 * @returns {GPUComputePipeline} 计算管线
 */
export function createGuidedFilterPipeline(device, params, bindGroupLayout) {
  const shaderCode = generateGuidedFilterShader(params);
  
  const shaderModule = device.createShaderModule({
    code: shaderCode
  });
  
  return device.createComputePipeline({
    layout: device.createPipelineLayout({
      bindGroupLayouts: [bindGroupLayout]
    }),
    compute: {
      module: shaderModule,
      entryPoint: 'main'
    },
    label: 'GuidedFilter Pipeline'
  });
}

/**
 * 创建细节提取计算管线
 * @param {GPUDevice} device - WebGPU设备
 * @param {GPUBindGroupLayout} bindGroupLayout - BindGroup布局
 * @returns {GPUComputePipeline} 计算管线
 */
export function createDetailExtractionPipeline(device, bindGroupLayout) {
  const shaderCode = generateDetailExtractionShader();
  
  console.log('Detail Extraction shader code:', shaderCode);
  
  const shaderModule = device.createShaderModule({
    code: shaderCode
  });
  
  return device.createComputePipeline({
    layout: device.createPipelineLayout({
      bindGroupLayouts: [bindGroupLayout]
    }),
    compute: {
      module: shaderModule,
      entryPoint: 'main'
    },
    label: 'Detail Extraction Pipeline'
  });
}

/**
 * 创建Guided Filter BindGroup布局
 * @param {GPUDevice} device - WebGPU设备
 * @returns {GPUBindGroupLayout} BindGroup布局
 */
export function createGuidedFilterBindGroupLayout(device) {
  return device.createBindGroupLayout({
    entries: [
      // 输入纹理 (binding 0)
      {
        binding: 0,
        visibility: GPUShaderStage.COMPUTE,
        texture: {
          sampleType: 'unfilterable-float'
        }
      },
      // 输出纹理 (binding 1)
      {
        binding: 1,
        visibility: GPUShaderStage.COMPUTE,
        storageTexture: {
          access: 'write-only',
          format: 'rgba32float'
        }
      },
      // 参数缓冲区 (binding 2)
      {
        binding: 2,
        visibility: GPUShaderStage.COMPUTE,
        buffer: {
          type: 'uniform'
        }
      }
    ],
    label: 'GuidedFilter Params BindGroup'
  });
}

/**
 * 创建细节提取BindGroup布局
 * @param {GPUDevice} device - WebGPU设备
 * @returns {GPUBindGroupLayout} BindGroup布局
 */
export function createDetailExtractionBindGroupLayout(device) {
  return device.createBindGroupLayout({
    entries: [
      // 输入纹理 (binding 0)
      {
        binding: 0,
        visibility: GPUShaderStage.COMPUTE,
        texture: {
          sampleType: 'unfilterable-float'
        }
      },
      // base纹理 (binding 1)
      {
        binding: 1,
        visibility: GPUShaderStage.COMPUTE,
        texture: {
          sampleType: 'unfilterable-float'
        }
      },
      // 参数缓冲区 (binding 2)
      {
        binding: 2,
        visibility: GPUShaderStage.COMPUTE,
        buffer: {
          type: 'uniform'
        }
      },
      // 输出纹理 (binding 3)
      {
        binding: 3,
        visibility: GPUShaderStage.COMPUTE,
        storageTexture: {
          access: 'write-only',
          format: 'rgba32float'
        }
      }
    ],
    label: 'Detail Extraction Params BindGroup'
  });
}

/**
 * 创建Guided Filter BindGroup
 * @param {GPUDevice} device - WebGPU设备
 * @param {GPUBindGroupLayout} layout - BindGroup布局
 * @param {Object} resources - 资源对象
 * @returns {GPUBindGroup} BindGroup
 */
export function createGuidedFilterBindGroup(device, layout, resources) {
  const { inputTexture, outputTexture, uniformBuffer } = resources;
  
  return device.createBindGroup({
    layout,
    entries: [
      // 输入纹理
      {
        binding: 0,
        resource: inputTexture.createView()
      },
      // 输出纹理
      {
        binding: 1,
        resource: outputTexture.createView()
      },
      // 参数缓冲区
      {
        binding: 2,
        resource: uniformBuffer
      }
    ],
    label: 'GuidedFilter BindGroup'
  });
}

/**
 * 创建细节提取BindGroup
 * @param {GPUDevice} device - WebGPU设备
 * @param {GPUBindGroupLayout} layout - BindGroup布局
 * @param {Object} resources - 资源对象
 * @returns {GPUBindGroup} BindGroup
 */
export function createDetailExtractionBindGroup(device, layout, resources) {
  const { inputTexture, baseTexture, outputTexture, uniformBuffer } = resources;
  
  return device.createBindGroup({
    layout,
    entries: [
      // 输入纹理
      {
        binding: 0,
        resource: inputTexture.createView()
      },
      // base纹理
      {
        binding: 1,
        resource: baseTexture.createView()
      },
      // 参数缓冲区
      {
        binding: 2,
        resource: uniformBuffer
      },
      // 输出纹理
      {
        binding: 3,
        resource: outputTexture.createView()
      }
    ],
    label: 'Detail Extraction BindGroup'
  });
} 